/// «Товари» — every word of the section, in its own file so that it merges
/// cleanly next to other sections (uk.ts carries one line for it).

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/// «24 товари пораховано, 3 розбіжності» — a count in words, plurals right.
const countedWords = (counted: number, moved: number): string =>
  `${counted} ${plural(counted, 'товар', 'товари', 'товарів')} пораховано, ${
    moved === 0 ? 'розбіжностей немає' : `${moved} ${plural(moved, 'розбіжність', 'розбіжності', 'розбіжностей')}`
  }`

export const goodsUk = {
  nav: 'Товари',
  title: 'Товари',
  subtitle: 'Що продає клуб, скільки лишилося на полиці й кожен документ, що це змінив.',
  // One word for selling across the section: «Продаж» is a paper and the
  // verb's button, «Продажі» are many of them — the report and the filter.
  // «Реалізація» was accounting jargon on a tab next to them.
  tabs: { stock: 'Залишки', sales: 'Продажі', documents: 'Документи' },

  actions: {
    sell: 'Продаж',
    receive: 'Прихід',
    writeOff: 'Списання',
    count: 'Інвентаризація',
    newProduct: 'Новий товар',
    edit: 'Змінити',
    retire: 'Зняти з продажу',
    writeOffLeft: 'Списати залишок',
    /// A written paper is never changed (S8): the opposite one, with its
    /// lines, undoes the mistake.
    correct: 'Виправити',
    more: 'Ще',
  },

  stock: {
    search: 'Назва, категорія або штрихкод',
    filterLabel: 'Залишки',
    filters: { all: 'Усі', low: 'Закінчуються', out: 'Немає', retired: 'Зняті' } as Record<string, string>,
    columns: {
      name: 'Товар',
      onHand: 'Залишок',
      min: 'Мінімум',
      price: 'Ціна',
      // `lastCostMinor` is the last receipt's price, not a cost basis
      // (specs/shop.md S7) — the label says exactly that.
      cost: 'Остання закупівля',
      value: 'Оцінка залишку',
    },
    state: { ok: 'Є', low: 'Закінчується', out: 'Немає', retired: 'Знято' } as Record<string, string>,
    summary: {
      products: 'Товарів у продажу',
      value: 'Залишок, оцінка',
      /// An estimate: every unit at its product's LAST purchase price.
      approx: (money: string) => `≈ ${money}`,
      valueNote: 'за останніми цінами закупівлі',
      valueUnpriced: (n: number) => `без ціни закупівлі: ${n}`,
      retiredWithStock: (n: number) => `у знятих із продажу: ${n} — спишіть`,
      // The pill's word, not «Час замовити»: one name for one list.
      low: 'Закінчуються',
      openLow: (n: number) => `Закінчуються: ${n}. Показати їх`,
    },
    empty: 'Ще немає жодного товару',
    emptyMessage: 'Додайте перший — воду, батончики, шейкери — і оформіть прихід.',
    // Roles are built per company; «власник або директор» is not who it is
    // everywhere.
    emptyReadOnly: 'Товари додає той, хто керує складом усієї компанії.',
    emptyFilter: 'Нічого не знайдено. Змініть пошук або фільтр.',
    /// Unknown (no purchase price) — the one thing «—» means in this list.
    noCost: '—',
    noMin: '—',
  },

  /// Which shelf: every one summed, the one with no club, a club
  /// (domain/shelves.ts).
  shelf: {
    label: 'Клуб',
    all: 'Усі клуби',
    none: 'Без клубу',
    unknownClub: 'Клуб',
    pick: 'Оберіть клуб',
    pickError: 'Оберіть клуб — документ ляже на його полицю',
    noneHint: 'Товар, прийнятий до відкриття першого клубу. Щоб перенести його в клуб, спишіть звідси й оприбуткуйте там.',
    nowhere: 'Немає відкритого клубу, куди ваша роль може провести документ.',
    failed: 'Не вдалося завантажити клуби, а без них не видно, на яку полицю ляже документ.',
  },

  product: {
    newTitle: 'Новий товар',
    /// Opened from a scan: the code is already in its field — the title
    /// does not repeat it (nor go stale when it is edited).
    fromScan: 'Штрихкод — з відсканованої етикетки. Назвіть товар і вкажіть ціну.',
    editTitle: 'Змінити товар',
    name: 'Назва',
    namePlaceholder: 'Вода 0,5 л',
    category: 'Категорія',
    categoryPlaceholder: 'Напої',
    categoryHint: 'Своя для клубу: напої, батончики, аксесуари.',
    unit: 'Одиниця',
    // Every count is a whole number of these — no kilograms or litres.
    unitHint: 'Як рахуєте, коротко: шт, уп., пляшка. Кількість — ціле число.',
    barcode: 'Штрихкод',
    barcodePlaceholder: 'Відскануйте або введіть',
    price: 'Ціна продажу',
    minStock: 'Мінімальний залишок',
    // What «Закінчуються» actually does: out of stock is listed there with or
    // without a minimum (domain/goods.ts `matchesFilter`).
    minStockHint:
      'Коли лишиться стільки або менше, товар потрапить у «Закінчуються». Порожньо — лише коли не лишиться зовсім.',
    save: 'Зберегти',
    create: 'Додати товар',
    saving: 'Зберігаємо…',
    errors: {
      name: 'Вкажіть назву',
      category: 'Вкажіть категорію',
      unit: 'Вкажіть одиницю',
      price: 'Вкажіть ціну числом, наприклад 45 або 45,50',
      minStock: 'Ціле число, 0 або більше',
      /// What services/shop takes (S5) — said before it refuses.
      barcode: 'Лише цифри й латинські літери, «-» чи «.», до 64 знаків, не самі нулі',
    },
    created: (name: string) => `«${name}» додано. Тепер оформіть прихід.`,
    saved: (name: string) => `«${name}» збережено.`,
    retireTitle: (name: string) => `Зняти «${name}» з продажу?`,
    // specs/shop.md S9: neither sold nor received after; retired only with
    // every shelf empty, so no goods are frozen on it.
    retireMessage: 'Його більше не можна буде ні продати, ні прийняти. Історія руху лишається.',
    retireHasStock: (n: number, unit: string) =>
      `На полицях ще ${n} ${unit}. Зняти товар можна, коли не лишиться нічого, — продайте або спишіть залишок.`,
    retired: (name: string) => `«${name}» знято з продажу.`,
  },

  doc: {
    kinds: {
      receipt: 'Прихід',
      write_off: 'Списання',
      sale: 'Продаж',
      stocktake: 'Інвентаризація',
      surplus: 'Інвентаризація · надлишок',
      shortage: 'Інвентаризація · нестача',
    } as Record<string, string>,
    receiptTitle: 'Прихід товару',
    receiptDescription: 'Товар прийшов від постачальника — він додасться до залишку.',
    writeOffTitle: 'Списання',
    writeOffDescription: 'Товар зіпсувався, загубився чи пішов на потреби клубу — він зникне із залишку.',
    supplier: 'Постачальник',
    supplierPlaceholder: 'ТОВ «Моршинська»',
    documentNo: 'Номер накладної',
    documentNoPlaceholder: 'ПН-0017',
    date: 'Дата накладної',
    dateHint: 'Якщо товар прийшов не сьогодні — дата з накладної.',
    reason: 'Причина',
    reasons: {
      damaged: 'Пошкоджено',
      expired: 'Минув термін',
      own_use: 'На потреби клубу',
      // «Нестача» is what a stocktake finds; a write-off of a known loss
      // says what happened.
      lost: 'Загублено',
      other: 'Інше',
      correction: 'Виправлення помилки',
    } as Record<string, string>,
    note: 'Примітка',
    lines: 'Товари',
    product: 'Товар',
    pickProduct: 'Оберіть товар',
    qty: 'Кількість',
    cost: 'Ціна закупівлі',
    available: (n: number, unit: string) => `На полиці: ${n} ${unit}`,
    addLine: 'Додати товар',
    removeLine: 'Прибрати рядок',
    total: 'Разом',
    atCost: 'Оцінка за останніми цінами закупівлі',
    problems: {
      product: 'Оберіть товар',
      qty: 'Ціле число від 1',
      unit: 'Ціна числом, наприклад 20 або 19,50',
      short: 'Більше, ніж є на полиці',
      discount: 'Знижка більша, ніж дозволено вашій ролі',
      zero: 'За 0 ₴ продає лише роль із правом «Продажі: керування». Зіпсований товар — це списання.',
    } as Record<string, string>,
    /// The shelf's figure stays in the words — it is what fixes the line.
    short: (n: number, unit: string) => `Більше, ніж є на полиці — є ${n} ${unit}`,
    noProducts: 'Спочатку додайте товар.',
    /// A new club's first receipt: the scan is how its first product is made.
    noProductsScan: 'Товарів ще немає. Відскануйте штрихкод — і створите товар просто тут.',
    shelfEmpty: 'На цій полиці нічого немає — списувати нічого.',
    /// The first step's button: the paper is read once more before it is
    /// written (S8 — it cannot be changed after).
    next: 'Далі',
    submitReceipt: 'Оприбуткувати',
    submitWriteOff: 'Списати',
    submitting: 'Проводимо…',
    retry: 'Повторити',
    reviewDescription: 'Перевірте ще раз: проведений документ не змінюється, помилку виправляє інший документ.',
    back: 'Назад до форми',
    /// What the server recorded — the first paper on a replay.
    received: (n: number, total: string | null) =>
      `Прихід проведено: ${n} ${plural(n, 'позиція', 'позиції', 'позицій')}${total ? ` на ${total}` : ''}.`,
    writtenOff: (n: number) => `Списано: ${n} ${plural(n, 'позиція', 'позиції', 'позицій')}.`,
    /// «48 пляшка × 19,50 ₴», «3 шт».
    lineDetail: (qty: number, unit: string, price: string | null) => (price ? `${qty} ${unit} × ${price}` : `${qty} ${unit}`),
    correctsReceipt: (no: string | null, date: string) =>
      no ? `Виправлення приходу № ${no} від ${date}` : `Виправлення приходу від ${date}`,
    correctsWriteOff: (date: string) => `Виправлення списання від ${date}`,
  },

  /// A barcode scanner at a paper (app/goods/scan-bar.tsx). Nothing here
  /// claims a scanner is connected — the panel cannot know; it says what a
  /// scan does, and what came of the last one, in a sentence.
  scan: {
    hint: 'Відскануйте штрихкод або введіть код з етикетки.',
    manual: 'Код з етикетки',
    add: 'Додати',
    /// What the scan did to its line — the quantity on screen now.
    added: (name: string, qty: string, unit: string) => `«${name}» — тепер ${qty} ${unit}.`,
    addedLine: (name: string) => `«${name}» додано.`,
    /// The line's quantity is not a whole number: the scan does not «fix» it.
    fixQty: (name: string) => `Кількість у рядку «${name}» — не ціле число. Виправте її, і скан додасть ще одну.`,
    /// At a receipt: the normal way a new product comes in — not an error.
    unknownNew: (code: string) => `Товару зі штрихкодом ${code} ще немає.`,
    unknown: (code: string) => `Штрихкод ${code} не знайдено серед товарів.`,
    /// The check digit does not add up and no product has the code.
    misread: 'Код зчитано з помилкою — відскануйте ще раз.',
    mistyped: 'У коді помилка: остання, контрольна, цифра не збігається. Перевірте цифри.',
    /// A scan that broke off or came too short.
    unreadable: 'Не вдалося прочитати код — відскануйте ще раз або введіть цифри вручну.',
    notACode: 'Штрихкод — це цифри й латинські літери. Перевірте, що введено.',
    retired: (name: string) => `«${name}» знято з продажу.`,
    noShelf: 'Спершу виберіть клуб — тоді скан додасть товар.',
    looking: (code: string) => `Шукаємо ${code}…`,
    /// Not «не знайдено»: nobody looked. Retry, or type it by hand later.
    offline: 'Немає звʼязку — код не перевірено. Спробуйте ще раз.',
    forbidden: 'Ваша роль не бачить товарів цієї компанії.',
    failed: 'Сервер не відповів — код не перевірено. Спробуйте ще раз.',
    createProduct: 'Створити товар',
  },

  sale: {
    title: 'Продаж товару',
    description: 'Товар іде з полиці за гроші — готівкою, карткою або готівкою й карткою разом.',
    reviewDescription: 'Перевірте ще раз: проведений продаж не змінюється.',
    client: 'Клієнт',
    addClient: 'Вказати клієнта',
    clientOptional: 'Необовʼязково. З клієнтом продаж видно в його картці.',
    removeClient: 'Прибрати клієнта',
    price: 'Ціна',
    /// The limit stays in the words — it is what fixes the line.
    overLimit: (pct: number) => `Знижка більша, ніж дозволено вашій ролі, — не більше ${pct} %`,
    /// Under a line priced below the list: «знижка 10 % від 75 ₴».
    discount: (pct: string, list: string) => `знижка ${pct} % від ${list}`,
    tender: 'Оплата',
    // «Частинами» in a Ukrainian shop is instalments; this is two tenders.
    tenders: { cash: 'Готівка', card: 'Картка', mixed: 'Готівка + картка' } as Record<string, string>,
    cash: 'Готівкою',
    cardRest: (amount: string) => `Карткою — ${amount}`,
    cashProblems: {
      empty: 'Вкажіть, скільки готівкою',
      number: 'Сума числом, наприклад 200 або 199,50',
    } as Record<string, string>,
    cashTooMuch: (total: string) => `Готівкою не більше, ніж разом, — ${total}`,
    /// Change is counted here and handed out by the desk; the server keeps
    /// only what the sale cost (specs/shop.md S4).
    given: 'Отримано готівкою',
    givenHint: 'Необовʼязково — щоб порахувати решту.',
    givenInvalid: 'Сума числом, наприклад 500',
    change: (amount: string) => `Решта: ${amount}`,
    short: (amount: string) => `Бракує ${amount}`,
    paid: 'Оплата',
    paidCash: (amount: string) => `Готівка · ${amount}`,
    paidCard: (amount: string) => `Картка · ${amount}`,
    paidMixed: (cash: string, card: string) => `Готівка ${cash} + картка ${card}`,
    givenFact: 'Отримано',
    changeFact: 'Решта',
    submit: (amount: string) => `Продати · ${amount}`,
    /// What the server recorded — the first sale on a replay.
    sold: (amount: string) => `Продано на ${amount}.`,
    /// A retry found the sale already written (200): say so, or the desk
    /// takes the money twice.
    alreadySold: (amount: string) => `Цей продаж уже було проведено — на ${amount}. Удруге гроші не беріть.`,
    nothingOnShelf: 'На цій полиці немає товару для продажу.',
    /// Only to someone who may receive goods.
    nothingOnShelfReceive: 'На цій полиці немає товару для продажу — спочатку оформіть прихід.',
  },

  count: {
    title: 'Інвентаризація',
    // A club is ONE shelf to the papers (specs/shop.md §2.4): a figure is the
    // product's whole quantity in the club. «Можна перевірити одну полицю»
    // made a manager count the fridge's 12 bottles and post the storeroom's
    // 24 as a shortage.
    // What becomes of the difference is the review's to say, with figures —
    // here it took a phone's rows.
    description: 'Порахуйте кожен товар у всіх місцях клубу — на вітрині, у холодильнику, на складі — і впишіть загальну кількість.',
    // The stock list's own words — and they fit a phone's search field.
    search: 'Назва, категорія або штрихкод',
    columns: { name: 'Товар', book: 'За обліком', counted: 'Фактично', diff: 'Різниця' },
    /// A partial count is some of the PRODUCTS, never some of the places.
    partial:
      'Можна порахувати лише частину товарів, скажімо, напої: рядки без числа не враховуємо. «За обліком» — на мить, коли ви вписали число, тож продажі під час підрахунку не стануть нестачею.',
    /// Under the papers' figure a row was counted against (S15): it stays
    /// as it was when the first figure went in.
    bookAsOf: (when: string) => `станом на ${when}`,
    /// The papers moved since — a sale during the count. Not a shortage.
    bookNow: (n: number) => `зараз ${n}`,
    /// 409 `book_changed`: this row is counted again.
    recount: (was: number, now: number, unit: string) =>
      `Поки ви рахували, з полиці пішло більше, ніж ви нарахували: було ${was}, зараз ${now} ${unit}. Порахуйте ще раз.`,
    scanHint: 'Можна рахувати сканером: кожен скан — ще одна одиниця в рядку товару.',
    /// The count on screen now, after the scan.
    scanned: (name: string, n: string, unit: string) => `«${name}» — пораховано ${n} ${unit}.`,
    /// The row holds something that is not a whole count: the scan does not
    /// overwrite it with 1.
    scanFix: (name: string, typed: string) =>
      `У рядку «${name}» — «${typed}», а не ціле число. Виправте його, і скан рахуватиме далі.`,
    fillBook: 'Заповнити за обліком',
    summary: {
      counted: (n: number, of: number) => `Пораховано ${n} з ${of}`,
      matched: (n: number) => `збігається: ${n}`,
    },
    /// What a count found, two sides and never one net figure — a lost towel
    /// is not paid for by a bar found behind the fridge.
    sides: {
      shortage: 'Нестача',
      surplus: 'Надлишок',
      none: 'Розбіжностей немає',
      /// Products on a side with no purchase price: unknown, not 0 ₴.
      uncosted: (n: number) => `без собівартості: ${n}`,
      noCost: 'без собівартості',
    },
    invalid: 'Фактична кількість — ціле число, 0 або більше',
    /// The pills over the rows (domain/stocktake.ts `COUNT_FILTERS`).
    filterLabel: 'Рядки',
    filters: { all: 'Усі', uncounted: 'Непораховані', differences: 'Розбіжності' } as Record<string, string>,
    /// In place of the rows when none is shown.
    emptyRows: {
      search: 'Нічого не знайдено — змініть пошук.',
      uncounted: 'Усе пораховано.',
      differences: 'Розбіжностей немає.',
    },
    emptyShelf: 'На цій полиці немає товарів, які можна порахувати.',
    /// A count kept on this device from an earlier session.
    restored: (n: number, when: string) =>
      `Продовжуємо підрахунок, збережений о ${when}: ${n} ${plural(n, 'товар', 'товари', 'товарів')} уже пораховано.`,
    startOver: 'Почати заново',
    /// An attempt sent in an earlier session whose answer never came.
    resumed:
      'Минулого разу відповідь не дійшла — інвентаризація могла провестися. Натисніть «Повторити»: якщо вона вже є, ми покажемо її, а не проведемо вдруге.',
    /// Escape, ×, a click beside the panel, «Скасувати» — with counts typed.
    closing: {
      title: 'Закрити інвентаризацію?',
      kept: (n: number) =>
        `Пораховане (${n} ${plural(n, 'товар', 'товари', 'товарів')}) лишиться на цьому пристрої: відкрийте «Інвентаризацію» знову, щоб продовжити.`,
      lose: (n: number) =>
        `Браузер не дає зберегти чернетку, тож пораховане (${n} ${plural(n, 'товар', 'товари', 'товарів')}) зникне.`,
      lost: 'Відповідь не дійшла. Відкрийте «Інвентаризацію» знову й натисніть «Повторити» — вона не проведеться двічі.',
      confirm: 'Закрити',
      stay: 'Рахувати далі',
    },
    /// In the footer, after «Далі» — the first such row takes the focus.
    fix: (n: number) =>
      n === 1 ? 'Виправте рядок, підсвічений червоним.' : `Виправте ${n} ${plural(n, 'рядок', 'рядки', 'рядків')}, підсвічені червоним.`,
    /// Under a row, said as soon as it is typed — the server's limits.
    problems: { number: 'Ціле число, 0 або більше', tooBig: 'Не більше 1 000 000' } as Record<string, string>,
    /// A figure that reads like a slip: asked about, not refused.
    implausible: (book: number) =>
      `Набагато більше, ніж за обліком (${book}). Перевірте — чи не потрапив у поле штрихкод.`,
    /// The server's limit of products on one paper.
    tooManyRows: (n: number) =>
      `В одному документі — до 500 товарів, а пораховано ${n}. Проведіть частину зараз, решту — наступною інвентаризацією.`,
    nothingCounted: 'Вкажіть фактичну кількість хоча б для одного товару',
    /// The irreversible verb, on the review — the form's button is «Далі».
    submit: 'Провести інвентаризацію',
    back: 'Назад до підрахунку',
    reviewDescription: 'Перевірте ще раз: проведена інвентаризація не змінюється — помилку виправить наступна.',
    /// The review's question: what is about to be written, in one line.
    confirm: (n: number, sides: readonly string[]) =>
      `Провести: ${n} ${plural(n, 'товар', 'товари', 'товарів')}, ${sides.length > 0 ? sides.join(', ') : 'розбіжностей немає'}?`,
    confirmShortage: (units: string, money: string | null) => `нестача ${units}${money ? ` на ${money}` : ''}`,
    confirmSurplus: (units: string, money: string | null) => `надлишок ${units}${money ? ` на ${money}` : ''}`,
    /// Figures that read like a slip, listed on the review.
    check: 'Перевірте',
    checkRow: (name: string, counted: number, book: number) => `${name}: ${counted} при обліку ${book}`,
    notePlaceholder: 'Наприклад: квартальна, рахували Олена й Тарас',
    /// What the server recorded. «Усе збігається» claimed the whole shelf
    /// for a count of some of it.
    done: (counted: number, moved: number) => `Інвентаризацію проведено: ${countedWords(counted, moved)}.`,
    alreadyDone: (counted: number, moved: number) => `Цю інвентаризацію вже було проведено: ${countedWords(counted, moved)}.`,
    view: {
      shortage: 'Нестача',
      surplus: 'Надлишок',
      allMatched: 'Розбіжностей не знайдено — усе збігалося з обліком.',
      /// A line the shelf never had a purchase price for (S13).
      noCost: 'без собівартості',
      found: 'Розбіжності',
      table: 'Підрахунок',
      counted: 'Пораховано',
      /// A row's two figures under its name, on a phone.
      figures: (book: number, counted: number, unit: string) => `за обліком ${book} · фактично ${counted} ${unit}`,
      /// Short enough to stand beside «Пораховано» on a phone.
      countedValue: (n: number, matched: number) => `${n} ${plural(n, 'товар', 'товари', 'товарів')} · збіглося ${matched}`,
      /// The papers moved during the count: what they said when it was
      /// written — the gap was a sale or a delivery, not a shortage (S15).
      atPosting: (n: number) => `при проведенні — ${n}`,
      failed: 'Не вдалося завантажити, що саме пораховано.',
    },
    /// A stocktake on the list of papers: what it covered.
    listItems: (n: number, moved: number) =>
      `${n} ${plural(n, 'товар', 'товари', 'товарів')} · ${
        moved === 0 ? 'без розбіжностей' : `${moved} ${plural(moved, 'розбіжність', 'розбіжності', 'розбіжностей')}`
      }`,
  },

  report: {
    period: 'Період',
    periods: { today: 'Сьогодні', week: '7 днів', month: '30 днів', thisMonth: 'Цей місяць', lastMonth: 'Минулий місяць' } as Record<string, string>,
    revenue: 'Виручка',
    // Not a costing method yet (specs/shop.md §2.6, S13): the figure says
    // which price it is at — under it on the tile, under the table.
    cost: 'Собівартість',
    costMethod: 'за останнім приходом',
    costFootnote: 'Собівартість — за останнім приходом на полиці до продажу. «—» — ціна закупівлі не відома, «≈» — оцінка.',
    margin: 'Маржа',
    /// Of the revenue the margin is measured against — the costed lines'.
    marginShare: (percent: number) => `${percent} % від виручки з відомою собівартістю`,
    sales: 'Продажі',
    /// On a phone the margin is a word in the row's second line.
    marginShort: 'маржа',
    cashCard: (cash: string, card: string) => `готівкою ${cash} · карткою ${card}`,
    /// Under the revenue: what discounts under the list gave away (S12).
    discounts: (amount: string) => `знижки ${amount}`,
    byProduct: 'За товарами',
    byCashier: 'Гроші за касирами',
    /// The goods' cash is not in the cash desk's shifts yet (E6).
    cashierNote: 'Готівка за товари поки не потрапляє в «Касу» — звіряйте шухляду з цими сумами.',
    cashierColumns: { who: 'Хто продавав', sales: 'Продажі', cash: 'Готівка', card: 'Картка' },
    columns: {
      name: 'Товар',
      qty: 'Продано',
      revenue: 'Виручка',
      discount: 'Знижки',
      cost: 'Собівартість',
      margin: 'Маржа',
      share: 'Маржа, %',
    },
    // A sale keeps the cost it had when it sold (S13): a later receipt's
    // price does not reach back, so the advice is about the next sales.
    uncosted: (n: number) =>
      `${n} ${plural(n, 'рядок', 'рядки', 'рядків')} продано, коли ціна закупівлі на цій полиці була невідома, — їхня собівартість невідома й такою лишиться. Вони є у виручці, але не в собівартості й не в маржі. Щоб наступні продажі її мали, вказуйте ціну в приході.`,
    /// Under a product partly sold with no known cost.
    uncostedShort: (n: number) => `без собівартості: ${n}`,
    estimated: (n: number) =>
      `≈ ${n} ${plural(n, 'рядок', 'рядки', 'рядків')} продано до того, як продаж почав фіксувати собівартість, — для них її оцінено за приходом на тій самій полиці до дати продажу.`,
    empty: 'За цей період продажів не було',
    emptyMessage: 'Оберіть інший період або оформіть продаж.',
    /// Revenue and margin are a report about money (specs/shop.md §4).
    noAccess: 'Виручку й маржу бачить роль із правом «Звіти» або «Фінанси».',
  },

  documents: {
    filterLabel: 'Документи',
    filters: { all: 'Усі', receipt: 'Приходи', write_off: 'Списання', sale: 'Продажі', stocktake: 'Інвентаризації' } as Record<string, string>,
    columns: { date: 'Дата', doc: 'Документ', club: 'Клуб', author: 'Провів', items: 'Товари', total: 'Сума' },
    empty: 'Документів ще немає',
    emptyMessage: 'Тут зʼявиться кожен прихід, списання й продаж.',
    emptyFilter: 'Таких документів немає',
    noPapers: 'Документи бачить роль із доступом до складу. Вашій ролі видно лише залишки на полиці.',
    /// The author of a paper who is not among today's members: someone who
    /// left, or eatlete's staff acting from outside.
    outsider: 'Не з команди клубу',
    view: {
      lines: 'Товари',
      supplier: 'Постачальник',
      documentNo: 'Накладна',
      reason: 'Причина',
      club: 'Клуб',
      client: 'Клієнт',
      /// A client is on the sale, but this role reads no clients.
      clientHidden: 'Є в картці клієнта',
      author: 'Провів',
      paid: 'Оплата',
      cash: 'Готівка',
      card: 'Картка',
      note: 'Примітка',
    },
    /// A list cut into pages says so, and fetches the rest on request.
    shown: (n: number) => `Показано ${n}`,
    all: (n: number) => `Усього ${n}`,
    more: 'Показати ще',
    failed: 'Не вдалося відкрити документ.',
  },

  /// The goods section of a client's card.
  purchases: {
    title: 'Покупки товарів',
    empty: 'Товарів ще не купував — або купував без картки.',
    failed: 'Не вдалося завантажити покупки.',
  },

  card: {
    back: 'Товари',
    onHand: 'Залишок',
    min: 'Мінімум',
    value: 'Оцінка залишку',
    price: 'Ціна продажу',
    cost: 'Остання ціна закупівлі',
    barcode: 'Штрихкод',
    margin: 'Націнка',
    /// Measured against the last purchase price — as estimated as it is.
    marginValue: (percent: number) => `≈ ${percent} %`,
    /// No reorder line: the product is in «Закінчуються» only once it is out.
    noMin: 'Не задано',
    /// No receipt said what it cost — unknown, not zero.
    unknown: 'Невідомо',
    history: 'Рух товару',
    columns: { date: 'Дата', what: 'Операція', qty: 'Кількість', balance: 'Залишок' },
    empty: 'Руху ще не було',
    emptyMessage: 'Оформіть прихід — і залишок зʼявиться.',
    invoice: (no: string) => `№ ${no}`,
    notFound: 'Такого товару немає',
  },

  errors: {
    insufficientStock: 'На полиці менше, ніж у документі. Залишки оновлено — перевірте кількість.',
    /// S15: the papers moved since some products were counted, in a way the
    /// count cannot explain. The rows are marked; nothing was written.
    bookChanged: 'Поки ви рахували, облік деяких товарів змінився так, що підрахунок не сходиться. Їх позначено — порахуйте ще раз.',
    /// A paper's money past what one paper can say: a typo, not a sale.
    amountOutOfRange: 'Сума документа завелика — так не буває. Перевірте кількості: можливо, в поле потрапив штрихкод.',
    productRetired: 'Цей товар знято з продажу — його не приймають і не продають.',
    productHasStock: (onHand: number | null) =>
      onHand === null
        ? 'На полицях ще є цей товар. Продайте або спишіть залишок — тоді його можна зняти.'
        : `На полицях ще ${onHand}. Продайте або спишіть залишок — тоді товар можна зняти.`,
    unknownProduct: 'Такого товару немає',
    /// S5: names the product that has the code, so it can be found.
    barcodeTaken: (name: string | null) =>
      name === null ? 'Такий штрихкод уже має інший товар у продажу.' : `Такий штрихкод уже має «${name}».`,
    invalidBarcode: 'Штрихкод — це цифри й латинські літери, а також «-» чи «.», до 64 знаків.',
    tenderMismatch: 'Сума оплати не збігається із сумою продажу',
    discountOverLimit: (pct: number | null) =>
      pct === null
        ? 'Знижка більша, ніж дозволено вашій ролі. Продайте за ціною з прайсу або попросіть того, хто керує продажами.'
        : `Знижка більша, ніж дозволено вашій ролі, — не більше ${pct} %. Продайте дорожче або попросіть того, хто керує продажами.`,
    zeroPrice: 'За 0 ₴ продає лише роль із правом «Продажі: керування». Зіпсований чи використаний товар — це списання.',
    backdated: (hours: number | null) =>
      `Продаж, датований раніше ніж ${hours ?? 72} години тому, проводить лише роль із правом «Продажі: керування».`,
    companyScope: 'Номенклатуру й документи без клубу веде лише роль з охопленням усієї компанії.',
    outOfScope: 'Цей клуб поза охопленням вашої ролі.',
    unknownDocument: 'Такого документа немає',
    keyReused:
      'Цю спробу вже проведено з іншими даними. Закрийте панель і перевірте «Документи», перш ніж проводити ще раз.',
    stockManage: 'Списання й номенклатура — для ролі з правом «Склад: керування».',
    reportsView: 'Звіт продажів — для ролі з правом «Звіти» або «Фінанси».',
    /// The answer never came; the key makes «Повторити» safe — say so, or the
    /// desk opens a new form and receives the pallet twice.
    lostAnswer:
      'Відповідь не дійшла — документ міг провестися. Натисніть «Повторити» в цій формі: якщо він уже є, ми покажемо його, а не проведемо вдруге.',
  },
}
